/**
 * CashFlowView - NVIDIA Financials > Cash Flow page view
 *
 * Displays cash flow statement data with timeline controls
 */

import { BaseView } from './BaseView';
import { DownloadService } from '../services/DownloadService';
import { StorageService } from '../services/StorageService';

export class CashFlowView extends BaseView {
	private downloadService: DownloadService;
	private storageService: StorageService;

	constructor() {
		super();
		this.downloadService = new DownloadService();
		this.storageService = new StorageService();
	}
	/**
	 * Render the cash flow view content
	 */
	render(): string {
		return `
			<div class="company-header">
				<div class="company-name"><a href="/profile">NVIDIA Corporation (NasdaqGS:NVDA)</a> Financials &gt; Cash Flow</div>
			</div>
			<div class="button-row">
				<div class="tool-button download-button">
					<img src="../img/icon1.png" alt="Download Financials" onerror="this.src='../img/icon-placeholder.png'">
					<span class="button-text">Download Financials</span>
				</div>
				<div class="tool-button pdf-button" data-pdf="../files/NVIDIA-10-K-08-27-2025.pdf">
					<img src="../img/icon3.png" alt="10-K" onerror="this.src='../img/icon-placeholder.png'">
					<span class="button-text">10-K</span>
				</div>
				<div class="tool-button pdf-button" data-pdf="../files/NVIDIA-10-Q-08-27-2025.pdf">
					<img src="../img/icon4.png" alt="10-Q" onerror="this.src='../img/icon-placeholder.png'">
					<span class="button-text">10-Q</span>
				</div>
			</div>
			<div class="tabs-container">
				<div class="tab-group">
					<a href="/financials" class="tab">Key Stats</a>
					<a href="/income-statement" class="tab">Income Statement</a>
					<a href="/balance-sheet" class="tab">Balance Sheet</a>
					<a href="/cash-flow" class="tab active">Cash Flow</a>
				</div>
			</div>
			<div class="financials-controls">
				<div class="control-row">
					<div class="control-group">
						<span class="control-label">Template:</span>
						<select class="control-select">
							<option selected>Standard</option>
						</select>
					</div>
					<div class="control-group">
						<span class="control-label">Restatement:</span>
						<select class="control-select">
							<option selected>Latest Filings</option>
						</select>
					</div>
				</div>
				<div class="control-row">
					<div class="control-group">
						<span class="control-label">Period Type:</span>
						<select class="control-select">
							<option selected>Annual</option>
							<option>Quarterly</option>
							<option>Semi-Annual</option>
							<option>LTM</option>
						</select>
					</div>
					<div class="control-group">
						<span class="control-label">Order:</span>
						<select class="control-select">
							<option selected>Latest on Right</option>
						</select>
					</div>
					<button class="go-button">Go</button>
				</div>
			</div>
			<div class="timeline-wrapper">
				<div class="timeline-container">
					<div class="timeline-nav-left">
						<button type="button" class="timeline-arrow">◀</button>
					</div>
					<div class="timeline-track">
						<div class="year-ticks">
							<div class="tick" data-year="'99"></div>
							<div class="tick" data-year="'01"></div>
							<div class="tick" data-year="'03"></div>
							<div class="tick" data-year="'05"></div>
							<div class="tick" data-year="'07"></div>
							<div class="tick" data-year="'09"></div>
							<div class="tick" data-year="'11"></div>
							<div class="tick" data-year="'13"></div>
							<div class="tick" data-year="'15"></div>
							<div class="tick" data-year="'17"></div>
							<div class="tick" data-year="'19"></div>
							<div class="tick" data-year="'21"></div>
							<div class="tick" data-year="'23"></div>
							<div class="tick" data-year="'25"></div>
						</div>
						<div class="selection-area">
							<div class="handle left"></div>
							<div class="selected-range"></div>
							<div class="handle right"></div>
						</div>
					</div>
					<div class="timeline-nav-right">
						<button type="button" class="timeline-arrow">▶</button>
						<button type="button" class="view-all-button">View All</button>
						<button type="button" class="update-range-button">Update Year Range</button>
					</div>
				</div>
			</div>
			<div class="main-section">
				<h2>Cash Flow</h2>
				<div class="financials-table-container">
					<!-- Placeholder for Cash Flow table -->
					<p>Cash Flow data will appear here.</p>
				</div>
			</div>
		`;
	}

	/**
	 * Lifecycle hook - called after view is mounted
	 */
	protected onMount(): void {
		this.setupPdfButtons();
		this.initializeDownloadButton();
		this.initializeTimeline();
		this.triggerContentUpdate();
	}

	/**
	 * Initialize timeline selector
	 */
	private initializeTimeline(): void {
		// Use setTimeout to ensure DOM is fully rendered
		setTimeout(() => {
			const timelineSelector = window.__timelineSelector;
			if (timelineSelector !== null && timelineSelector !== undefined) {
				timelineSelector.initialize();
			} else {
				console.warn('[CashFlowView] TimelineSelector not available');
			}
		}, 0);
	}

	/**
	 * Trigger ContentManager to update content based on current state
	 * Explicitly pass 'cash-flow' to avoid race condition with router pathname updates
	 */
	private triggerContentUpdate(): void {
		const contentManager = window.__contentManager;
		if (contentManager !== null && contentManager !== undefined) {
			contentManager.updateContent(undefined, 'cash-flow');
		} else {
			console.warn('[CashFlowView] ContentManager not available');
		}
	}

	/**
	 * Set up PDF button click handlers
	 */
	private setupPdfButtons(): void {
		const pdfButtons = this.querySelectorAll<HTMLElement>('.pdf-button');

		pdfButtons.forEach((button) => {
			this.addEventListener(button, 'click', () => {
				const pdfPath = button.getAttribute('data-pdf');
				if (pdfPath) {
					window.open(pdfPath, '_blank');
				}
			});
		});
	}

	/**
	 * Set up event listener for download button
	 */
	private initializeDownloadButton(): void {
		const downloadButton = this.container?.querySelector('.download-button');

		if (!downloadButton) {
			console.warn('[CashFlowView] Download button not found in DOM');
			return;
		}

		this.addEventListener(downloadButton as HTMLElement, 'click', () => {
			this.handleDownloadClick();
		});
	}

	/**
	 * Handle download button click
	 * Downloads file based on current timeline range and period type
	 */
	private handleDownloadClick(): void {
		// Get current timeline range from storage
		const timelineRange = this.storageService.getTimelineRange();
		if (!timelineRange) {
			console.warn('Timeline range not available');
			return;
		}

		// Get current period type from storage
		const periodType = this.storageService.getPeriodType();

		// Trigger download using DownloadService with timeline and period criteria
		this.downloadService.downloadFinancialStatement({
			leftYear: timelineRange.leftYear,
			rightYear: timelineRange.rightYear,
			periodType
		});
	}
}
